import { apiClient } from '@/lib/api/client';
import type {
  Campaign,
  CarouselSlot,
  AdWallet,
  AdTransaction,
  CampaignFilters,
  CreateCampaignData,
  UpdateCampaignData,
  CampaignStats,
  AdTypeConfig,
} from '../types/ads.types';

interface ApiData<T> {
  data: T;
  message?: string;
}

interface CampaignListResponse {
  data: Campaign[];
  total: number;
  page: number;
  lastPage: number;
}

interface TransactionListResponse {
  data: AdTransaction[];
  total: number;
  page: number;
  lastPage: number;
}

export const adsService = {
  // Wallet - uses /ads/wallet endpoint
  async getWallet(): Promise<AdWallet | null> {
    const response = await apiClient.get<ApiData<AdWallet>>('/ads/wallet');
    return response.data?.data ?? null;
  },

  async rechargeWallet(amount: number, paymentMethod?: string): Promise<{ success: boolean; transactionId: string } | null> {
    const response = await apiClient.post<ApiData<{ success: boolean; transactionId: string }>>(
      '/ads/wallet/recharge',
      { amount, paymentMethod }
    );
    return response.data?.data ?? null;
  },

  async getWalletTransactions(filters?: { page?: number; limit?: number }): Promise<AdTransaction[]> {
    const params = new URLSearchParams();
    // page and limit are required by the server
    params.set('page', String(filters?.page || 1));
    params.set('limit', String(filters?.limit || 20));

    const response = await apiClient.get<ApiData<TransactionListResponse>>(
      `/ads/wallet/transactions?${params.toString()}`
    );
    return response.data?.data?.data ?? [];
  },

  // Campaigns - uses /ads/campaigns endpoint
  async getCampaigns(filters?: CampaignFilters): Promise<{ campaigns: Campaign[]; total: number; page: number; totalPages: number }> {
    const params = new URLSearchParams();
    // page and limit are required by the server
    const page = filters?.page || 1;
    const limit = filters?.limit || 10;
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (filters?.search) params.set('search', filters.search);
    if (filters?.type) params.set('type', filters.type);
    if (filters?.status) params.set('status', filters.status);

    const response = await apiClient.get<ApiData<CampaignListResponse>>(
      `/ads/campaigns?${params.toString()}`
    );
    const data = response.data?.data;
    return {
      campaigns: data?.data ?? [],
      total: data?.total ?? 0,
      page: data?.page ?? page,
      totalPages: data?.lastPage ?? 1,
    };
  },

  async getCampaign(refId: string): Promise<Campaign | null> {
    const response = await apiClient.get<ApiData<Campaign>>(`/ads/campaigns/${refId}`);
    return response.data?.data ?? null;
  },

  async createCampaign(data: CreateCampaignData, bannerImage?: File): Promise<Campaign | null> {
    // Ensure items is always an array
    const items = Array.isArray(data.items) ? data.items : [];

    // Ensure budget values are valid numbers
    const budgetEur = isNaN(data.budgetEur) || data.budgetEur < 0 ? 0 : data.budgetEur;
    const budgetUsd = isNaN(data.budgetUsd) || data.budgetUsd < 0 ? 0 : data.budgetUsd;
    const budgetKmf = isNaN(data.budgetKmf) || data.budgetKmf < 0 ? 0 : data.budgetKmf;

    // If there's a banner image, use FormData for multipart upload
    if (bannerImage) {
      const formData = new FormData();

      // Append all campaign data fields
      formData.append('name', data.name);
      formData.append('type', data.type);
      formData.append('budgetEur', String(budgetEur));
      formData.append('budgetUsd', String(budgetUsd));
      formData.append('budgetKmf', String(budgetKmf));
      formData.append('startDate', data.startDate);
      formData.append('endDate', data.endDate);

      // Optional fields
      if (data.dailyBudgetEur !== undefined && !isNaN(data.dailyBudgetEur)) {
        formData.append('dailyBudgetEur', String(data.dailyBudgetEur));
      }
      if (data.dailyBudgetUsd !== undefined && !isNaN(data.dailyBudgetUsd)) {
        formData.append('dailyBudgetUsd', String(data.dailyBudgetUsd));
      }
      if (data.dailyBudgetKmf !== undefined && !isNaN(data.dailyBudgetKmf)) {
        formData.append('dailyBudgetKmf', String(data.dailyBudgetKmf));
      }
      if (data.bidAmountEur !== undefined && !isNaN(data.bidAmountEur)) {
        formData.append('bidAmountEur', String(data.bidAmountEur));
      }
      if (data.bidAmountUsd !== undefined && !isNaN(data.bidAmountUsd)) {
        formData.append('bidAmountUsd', String(data.bidAmountUsd));
      }
      if (data.bidAmountKmf !== undefined && !isNaN(data.bidAmountKmf)) {
        formData.append('bidAmountKmf', String(data.bidAmountKmf));
      }
      if (data.targeting) {
        formData.append('targeting', JSON.stringify(data.targeting));
      }

      // Always include items as JSON array
      formData.append('items', JSON.stringify(items));

      // Append the banner image
      formData.append('banner', bannerImage);

      const response = await apiClient.post<ApiData<Campaign>>('/ads/campaigns', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data?.data ?? null;
    }

    // Standard JSON request for campaigns without banner
    const response = await apiClient.post<ApiData<Campaign>>('/ads/campaigns', {
      ...data,
      budgetEur,
      budgetUsd,
      budgetKmf,
      items,
    });
    return response.data?.data ?? null;
  },

  async updateCampaign(refId: string, data: UpdateCampaignData): Promise<Campaign | null> {
    const response = await apiClient.put<ApiData<Campaign>>(`/ads/campaigns/${refId}`, data);
    return response.data?.data ?? null;
  },

  async updateCampaignStatus(refId: string, status: 'active' | 'paused'): Promise<Campaign | null> {
    const response = await apiClient.patch<ApiData<Campaign>>(
      `/ads/campaigns/${refId}/status`,
      { status }
    );
    return response.data?.data ?? null;
  },

  async deleteCampaign(refId: string): Promise<void> {
    await apiClient.delete(`/ads/campaigns/${refId}`);
  },

  async getCampaignStats(refId: string): Promise<CampaignStats | null> {
    const response = await apiClient.get<ApiData<CampaignStats>>(
      `/ads/campaigns/${refId}/stats`
    );
    return response.data?.data ?? null;
  },

  // Carousel - uses /ads/carousel endpoint
  async getCarouselAvailability(filters?: { startDate?: string; endDate?: string; position?: number }): Promise<CarouselSlot[]> {
    const params = new URLSearchParams();
    if (filters?.startDate) params.set('startDate', filters.startDate);
    if (filters?.endDate) params.set('endDate', filters.endDate);
    if (filters?.position) params.set('position', String(filters.position));

    const response = await apiClient.get<ApiData<{ slots: CarouselSlot[] }>>(
      `/ads/carousel/availability?${params.toString()}`
    );
    // Backend returns { slots: [] } inside data
    return response.data?.data?.slots ?? [];
  },

  // Audience
  async getShopSubscribersCount(): Promise<{ count: number }> {
    const response = await apiClient.get<ApiData<{ count: number }>>(
      '/ads/audience/subscribers'
    );
    return response.data?.data ?? { count: 0 };
  },

  async getProductLikersCount(): Promise<{ count: number }> {
    const response = await apiClient.get<ApiData<{ count: number }>>(
      '/ads/audience/product-likers'
    );
    return response.data?.data ?? { count: 0 };
  },

  // Ad Type Configs
  async getAdTypeConfigs(): Promise<AdTypeConfig[]> {
    const response = await apiClient.get<ApiData<AdTypeConfig[]>>('/ads/types');
    return response.data?.data ?? [];
  },
};
